import React, { useContext, useEffect, useState } from 'react';
import { Box, TextField, Button, Typography, CircularProgress, Alert, FormControlLabel, Switch } from '@mui/material';

import { AuthContext } from "../utils/AuthContext";
import { useNavigate, useSearchParams } from 'react-router-dom';

import {  toast } from 'react-toastify';

import CheckIcon from '@mui/icons-material/Check';
import { resetPassword } from '../services/ResetPasswordService';



const ResetPassword = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  const [email, setEmail] = useState(searchParams.get('email') || '');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const [emailError, setEmailError] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [confirmPasswordError, setConfirmPasswordError] = useState('');

  const navigate = useNavigate();

  const { auth } = useContext(AuthContext);



  const [isLoading, setLoading] = useState(false);

  const [isErrorAlertShowing, setErrorAlertShowing] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const [passwordCriteria, setPasswordCriteria] = useState({
    hasUpperCase: false,
    hasLowerCase: false,
    hasNumber: false,
    hasSpecialChar: false,
    hasMinLength: false,
  });

  const [isFocused, setIsFocused] = useState(false);

  const [showPasswords, setShowPasswords] = useState(false);

  useEffect(() => {
    if (auth?.user) {
      handleNavigation(auth?.user);
    }
  }, []);


  function validate() {
    setEmailError('');
    setPasswordError('');
    setConfirmPasswordError('');


    let isValid = true;

    const email_trimmed = email.trim();
    const password_trimmed = password.trim();
    const confirm_password_trimmed = confirmPassword.trim();


    if (email_trimmed === '') {
      setEmailError('Email is required')
      isValid = false
    } else if (email_trimmed.length > 50) {
      setEmailError('Email is too long')
      isValid = false
    }


    if (password_trimmed === '') {
      setPasswordError('Password is required')
      isValid = false
    } else if (!passwordCriteria.hasUpperCase || !passwordCriteria.hasLowerCase
      || !passwordCriteria.hasNumber || !passwordCriteria.hasSpecialChar
      || !passwordCriteria.hasMinLength
    ) {
      setPasswordError('Your password is not strong.');

      isValid = false;
    } else if (password_trimmed.length > 50) {
      setPasswordError('Password is too long')
      isValid = false
    }

    if (confirm_password_trimmed.trim() === '') {
      setConfirmPasswordError('Confirm password is required');
      isValid = false;
    } else if (password_trimmed !== confirm_password_trimmed) {
      setConfirmPasswordError('Passwords are not the same');
      isValid = false;
    }




    return isValid;
  }

  function handleSubmit(event) {
    event.preventDefault();
    let isValid = validate()

    if (!isValid) {
      return
    }

    setErrorAlertShowing(false);


    handleResetPassword();
  }

  async function handleResetPassword() {
    try {
      setLoading(true);

      const reset_pass_token = searchParams.get('token');

      let data = {
        email: email,
        password: password,
        password_confirmation: confirmPassword,
        token: reset_pass_token
      };

      const res = await resetPassword({ data });

      toast.success(res.data.message, {
        autoClose: 2500,
        hideProgressBar: true,
        icon: ({ theme, type }) => <CheckIcon sx={{ color: '#3328BF' }} />,
        closeButton: false
      });

      handleNavigation();

    } catch (error) {
      setErrorMessage(error.response?.data?.message);
      setErrorAlertShowing(true);
    } finally {
      setLoading(false);
    }
  }

  function handleNavigation() {
    navigate('/login');
  }

  const validatePassword = (password) => {
    // Regex checks for each individual condition
    const hasUpperCase = /[A-Z]/.test(password);
    const hasLowerCase = /[a-z]/.test(password);
    const hasNumber = /\d/.test(password);
    const hasSpecialChar = /[@$!%*?&#]/.test(password);
    const hasMinLength = password.length >= 6;

    // Update state based on criteria
    setPasswordCriteria({
      hasUpperCase,
      hasLowerCase,
      hasNumber,
      hasSpecialChar,
      hasMinLength,
    });
  };


  const handlePasswordChange = (e) => {
    const password = e.target.value;
    setPassword(password);
    validatePassword(password);
  };

  const renderIcon = (isValid) => (
    <Box sx={{ display: "flex", alignItems: "center" }}>
      {isValid ? (
        <CheckIcon sx={{ color: "#3328BF" }} />
      ) : (
        <CheckIcon sx={{ color: "#9e9e9e" }} />
      )}
    </Box>
  );


  const handleFocus = () => {
    setIsFocused(true); // Set focused state to true when the input is focused
  };

  const handleBlur = () => {
    setIsFocused(false); // Set focused state to false when the input loses focus
  };

  // useEffect(() => {
  //     if (auth.token) {
  //         navigate('/overview');
  //     }
  // }, []);

  return (
    <Box sx={{
      overflow: 'hidden',
      height: { xs: '100vh', md: '100vh' },
      width: { xs: '100vw', md: '100vw' },
      display: 'flex',
      flexDirection: { xs: 'column', md: 'row' },
      background: '#fff',
      opacity: 0,
      animation: 'fadeInPage 1.5s ease forwards', // Fade-in effect for entire page
      '@keyframes fadeInPage': {
        '0%': { opacity: 0 },
        '100%': { opacity: 1 },
      },
      // backgroundImage: { xs: `url(${process.env.PUBLIC_URL + "/images/login-image.png"})`, md: 'none' },
      backgroundSize: 'cover',
      backgroundPosition: 'center',
    }}>


      {/* Left side with image (only shown on desktop) */}
      <Box sx={{
        flex: { xs: 0, md: 1 },
        display: { xs: 'none', md: 'flex' },
        justifyContent: 'center',
        alignItems: 'center',
        animation: 'fadeIn 1s ease-in-out', // Fade-in animation for background image
        '@keyframes fadeIn': {
          '0%': { opacity: 0 },
          '100%': { opacity: 1 },
        },
      }}>
        <img
          src={process.env.PUBLIC_URL + "/images/login-image.png"}
          alt="Login Illustration"
          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
        />
      </Box>

      {/* Right side with login form */}
      <Box sx={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        width: { xs: '100%', md: '50%' },
        backdropFilter: { xs: 'blur(8px)', md: 'none' }, // Optional blur for mobile background
        animation: 'fadeInUp 0.6s ease-out', // Fade-in and slide-up effect for form container
        '@keyframes fadeInUp': {
          '0%': { opacity: 0, transform: 'translateY(20px)' },
          '100%': { opacity: 1, transform: 'translateY(0)' },
        },
      }}>
        <Box sx={{ pl: { xs: 2, md: 3 }, pt: { xs: 5, md: 3 } }}>
          <Typography variant='h5' className="agent-title">
            Agent
          </Typography>
          <Typography variant='h5' sx={{ color: '#3328BF' }} className="connect-title">
            Connect+
          </Typography>
        </Box>

        <Box sx={{
          flex: { xs: .7, md: .9 },
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          px: { xs: 4, sm: 8, md: 15, lg: 25 },
        }}>
          {/* <ToastContainer /> */}

          <Typography variant="h4" sx={{ mb: 2 }}>
            Reset Password
          </Typography>

          {
            isErrorAlertShowing &&
            <Alert
              sx={{ mb: 2, }}
              severity="error"
              onClose={() => { setErrorAlertShowing(false) }}
            >{errorMessage}</Alert>
          }

          <form onSubmit={handleSubmit}>
            <TextField
              label="Email"
              variant="filled"
              fullWidth
              sx={{
                mb: 2,
                '& .MuiFilledInput-root': {
                  transition: 'border-color 0.3s ease, box-shadow 0.3s ease',
                  '&.Mui-focused': {
                    boxShadow: '0 0 5px rgba(51, 40, 191, 0.4)',
                  },
                },
              }}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Enter your email" // Placeholder text
              autoComplete='email'
              type='email'
              error={emailError !== ''}
              helperText={emailError}
              disabled
            />
            <Box>
              <TextField
                label="New Password"
                type={showPasswords ? "text" : "password"}
                variant="filled"
                fullWidth
                sx={{
                  mb: 2,
                  '& .MuiFilledInput-root': {
                    transition: 'border-color 0.3s ease, box-shadow 0.3s ease',
                    '&.Mui-focused': {
                      boxShadow: '0 0 5px rgba(51, 40, 191, 0.4)',
                    },
                  },
                }}
                value={password}
                onChange={handlePasswordChange}
                onFocus={handleFocus} // Trigger when input is focused
                onBlur={handleBlur} // Trigger when input loses focus
                placeholder="Enter your password"
                error={passwordError !== ''}
                helperText={passwordError}
                autoFocus
              />
              {isFocused && ( // Only show criteria when the TextField is focused
                <Box sx={{ mt: 0, mb: 2 }}>
                  <Typography variant="body2" sx={{ display: "flex", alignItems: "center" }}>
                    {renderIcon(passwordCriteria.hasUpperCase)}
                    <span style={{ marginLeft: 8 }}>At least one uppercase letter</span>
                  </Typography>
                  <Typography variant="body2" sx={{ display: "flex", alignItems: "center" }}>
                    {renderIcon(passwordCriteria.hasLowerCase)}
                    <span style={{ marginLeft: 8 }}>At least one lowercase letter</span>
                  </Typography>
                  <Typography variant="body2" sx={{ display: "flex", alignItems: "center" }}>
                    {renderIcon(passwordCriteria.hasNumber)}
                    <span style={{ marginLeft: 8 }}>At least one number</span>
                  </Typography>
                  <Typography variant="body2" sx={{ display: "flex", alignItems: "center" }}>
                    {renderIcon(passwordCriteria.hasSpecialChar)}
                    <span style={{ marginLeft: 8 }}>At least one special character</span>
                  </Typography>
                  <Typography variant="body2" sx={{ display: "flex", alignItems: "center" }}>
                    {renderIcon(passwordCriteria.hasMinLength)}
                    <span style={{ marginLeft: 8 }}>At least 6 characters</span>
                  </Typography>
                </Box>
              )}
            </Box>

            <TextField
              label="Confirm New Password"
              type={showPasswords ? "text" : "password"}
              variant="filled"
              fullWidth
              sx={{
                '& .MuiFilledInput-root': {
                  transition: 'border-color 0.3s ease, box-shadow 0.3s ease',
                  '&.Mui-focused': {
                    boxShadow: '0 0 5px rgba(51, 40, 191, 0.4)',
                  },
                },
              }}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Enter confirm password"
              error={confirmPasswordError !== ''}
              helperText={confirmPasswordError}
            />

            <FormControlLabel
              sx={{ mt: 1 }}
              control={<Switch checked={showPasswords} onChange={(e) => setShowPasswords(e.target.checked)} />}
              label="Show Passwords" />


            <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
              <Button
                disabled={isLoading}
                variant="contained"
                type='submit'
                sx={{
                  height: { xs: '45px', sm: '40px' },
                  width: { xs: '100%', sm: '100px' },
                  background: '#3328BF',
                  transition: 'all 0.3s ease',
                  position: 'relative',
                  overflow: 'hidden',
                  '&:hover': {
                    background: '#2a20a0',
                    transform: 'translateY(-2px)',
                    boxShadow: '0 4px 20px rgba(51, 40, 191, 0.2)',
                  },
                  '&:active': {
                    transform: 'translateY(1px)',
                  },
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    background: 'linear-gradient(120deg, transparent, rgba(255,255,255,0.2), transparent)',
                    transform: 'translateX(-100%)',
                  },
                  '&:hover::after': {
                    transform: 'translateX(100%)',
                    transition: 'transform 0.5s ease',
                  }
                }}
              >
                {/* {isLoading ? 'Loading...' : 'Login'} */}

                Reset
              </Button>

              {
                isLoading && <CircularProgress size={24} sx={{ ml: 2 }} />
              }

            </Box>

          </form>
        </Box>
      </Box>
    </Box>
  );
};

export default ResetPassword;
